import { ParsedPartner } from "@justhome/common";
import { Application, LeadCreationResponse } from "@justhome/api-core";
import {
  Dispatch,
  FC,
  PropsWithChildren,
  Reducer,
  createContext,
  useReducer,
} from "react";
import { ContactFormValues, FormValues, StepType } from "./types";

interface AppContextState {
  form?: FormValues;
  contactForm?: ContactFormValues;
  steps: StepType[];
  application?: Application;
  lead?: LeadCreationResponse;
  partner?: ParsedPartner;
}

type AppContextAction =
  | { type: "MERGE_FORM"; value: FormValues }
  | { type: "SET_CONTACT_FORM"; value: ContactFormValues }
  | {
      type: "SET_APPLICATION";
      application: Application;
    }
  | {
      type: "SET_LEAD";
      lead: LeadCreationResponse;
    }
  | {
      type: "ADD_STEP";
      step: StepType;
    }
  | {
      type: "SET_PARTNER";
      partner: ParsedPartner;
    }
  | {
      type: "POP_STEP";
    };

export interface AppContextValue {
  state: AppContextState;
  dispatch: Dispatch<AppContextAction>;
}

const defaultState: AppContextState = {
  steps: ["typeOfProperty"],
};

export const AppContext = createContext<AppContextValue>({
  state: defaultState,
  dispatch: () => {},
});

AppContext.displayName = "app";

const reducer: Reducer<AppContextState, AppContextAction> = (state, action) => {
  switch (action.type) {
    case "SET_PARTNER": {
      return {
        ...state,
        partner: action.partner,
      };
    }
    case "SET_CONTACT_FORM": {
      return {
        ...state,
        contactForm: action.value,
      };
    }
    case "MERGE_FORM":
      return {
        ...state,
        form: {
          ...state.form,
          ...action.value,
        },
      };
    case "ADD_STEP": {
      return {
        ...state,
        steps: [...state.steps, action.step],
      };
    }
    case "POP_STEP": {
      return {
        ...state,
        steps: state.steps.slice(0, -1),
      };
    }
    case "SET_APPLICATION": {
      return {
        ...state,
        application: action.application,
      };
    }
    case "SET_LEAD": {
      return {
        ...state,
        lead: action.lead,
      };
    }
  }
};

export const AppContextProvider: FC<
  PropsWithChildren<{ postalCode?: string | null }>
> = ({ children, postalCode }) => {
  const [state, dispatch] = useReducer(
    reducer,
    postalCode
      ? {
          ...defaultState,
          form: { postalCode } as FormValues,
        }
      : defaultState,
  );

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
};
